import React, { useState, useEffect, useMemo } from "react";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	Button,
	Alert,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	FormControlLabel,
	Checkbox,
	TablePagination,
	Grid,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
	Edit as EditIcon,
	Plus as PlusIcon,
	Search as SearchIcon,
	Calendar as CalendarIcon,
	Download as DownloadIcon,
} from "lucide-react";
import { Autocomplete } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAuth } from "../../contexts/AuthContext";
import { useSelectedAcademicYear } from "../../hooks/useSelectedAcademicYear";
import AcademicYearSelector from "../../components/common/AcademicYearSelector";
import { getIndianStates, getDistrictsForStateName } from "../../data/indiaStateDistrictOptions";
import { visitMatchesAcademicYear } from "../../utils/academicYear";
import {
	fetchActiveSchoolsForExpert,
	isActiveSchool,
} from "../../utils/employeeSchools";

const INDIAN_STATES = getIndianStates();

const formatDateTime = (date) => {
	return date ? dayjs(date).format("DD/MM/YYYY HH:mm") : "-";
};

const SchoolVisits = () => {
	const { user, supabase } = useAuth();
	const { selectedAcademicYear, setSelectedAcademicYear } =
		useSelectedAcademicYear();
	const [isLoading, setIsLoading] = useState(true);
	const [visits, setVisits] = useState([]);
	const [filteredVisits, setFilteredVisits] = useState([]);
	const [schoolTypes, setSchoolTypes] = useState([]);
	const [schools, setSchools] = useState([]);
	const [userAssociatedSchools, setUserAssociatedSchools] = useState([]);
	const [profiles, setProfiles] = useState([]);
	const [visitTypes, setVisitTypes] = useState([]);
	const [visitStatuses, setVisitStatuses] = useState([]);
	const [salesProgressionStages, setSalesProgressionStages] = useState([]);
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedVisit, setSelectedVisit] = useState(null);
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [validationError, setValidationError] = useState("");
	const [userProfile, setUserProfile] = useState(null);

	const [visitTypeCategories] = useState(["Service", "Sales"]);
	const [selectedSchool, setSelectedSchool] = useState(null);
	const [isSaving, setIsSaving] = useState(false);

	const [openAddSchoolDialog, setOpenAddSchoolDialog] = useState(false);
	const [newSchoolForm, setNewSchoolForm] = useState({
		name: "",
		state: "",
		district: "",
		address: "",
	});
	const [newSchoolErrors, setNewSchoolErrors] = useState({
		name: false,
		state: false,
		district: false,
		address: false,
	});

	const newSchoolDistrictOptions = useMemo(
		() => getDistrictsForStateName(newSchoolForm.state),
		[newSchoolForm.state]
	);

	const getRowBackgroundColor = (visit) => {
		if (!visit.planned_date) return "transparent";

		const today = new Date();
		const plannedDate = new Date(visit.planned_date);
		const visitedDate = visit.visited_date
			? new Date(visit.visited_date)
			: null;

		// Reset time to compare only dates
		today.setHours(0, 0, 0, 0);
		plannedDate.setHours(0, 0, 0, 0);
		if (visitedDate) visitedDate.setHours(0, 0, 0, 0);

		// If today's date is greater than planned date and visited date is not present - red background
		if (today > plannedDate && !visitedDate) {
			return "#ffebee"; // Light red
		}

		// If visited date is greater than planned date - orange background
		if (visitedDate && visitedDate > plannedDate) {
			return "#fff3e0"; // Light orange
		}

		return "transparent";
	};

	const handleAddSchoolClick = () => {
		setNewSchoolForm({
			name: "",
			state: "",
			district: "",
			address: "",
		});
		setNewSchoolErrors({
			name: false,
			state: false,
			district: false,
			address: false,
		});
		setOpenAddSchoolDialog(true);
	};

	const handleSaveNewSchool = async () => {
		try {
			setIsSaving(true); // Add this line at the beginning

			// Validate required fields
			const errors = {
				name: !newSchoolForm.name.trim(),
				state: !newSchoolForm.state,
				district: !newSchoolForm.district,
				address: !newSchoolForm.address.trim(),
			};

			setNewSchoolErrors(errors);

			if (errors.name || errors.state || errors.district || errors.address) {
				return;
			}

			// Find the "Prospective School" type ID
			const prospectiveSchoolType = schoolTypes.find(
				(type) => type.name === "Prospective School"
			);

			const schoolData = {
				name: newSchoolForm.name,
				state: newSchoolForm.state,
				district: newSchoolForm.district,
				address: newSchoolForm.address,
				school_type_id: prospectiveSchoolType?.id || editForm.school_type_id,
				status: "Pending",
			};

			const { data, error } = await supabase
				.from("schools")
				.insert([schoolData])
				.select();

			if (error) throw error;

			// Update the schools list and select the new school
			const newSchool = data[0];
			setSchools((prev) => [...prev, newSchool]);
			setEditForm((prev) => ({ ...prev, school_id: newSchool.id }));
			setSelectedSchool(newSchool);

			setSuccessMessage("School added successfully");
			setOpenAddSchoolDialog(false);

			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error adding new school:", err);
			setErrorMessage("Error adding new school");
		} finally {
			setIsSaving(false); // Add this line in the finally block
		}
	};

	const [filters, setFilters] = useState({
		search: "",
		visitStatus: "",
		dateRange: null,
	});

	const [editForm, setEditForm] = useState({
		school_type_id: "",
		school_id: "",
		planned_by: "",
		planned_date: null,
		visit_type: "",
		visit_type_id: "",
		visit_status_id: "",
		visited_date: null,
		visited_by: "",
		sales_progression_stage_id: "",
		existing_school_remarks: "",
		new_school_remarks: "",
		oa_report_shared: false,
		assessment_qp_collected: false,
		remarks: "",
		comments: "",
	});

	useEffect(() => {
		fetchUserProfile();
		// fetchVisits();
		fetchSchoolTypes();
		fetchSchools();
		fetchUserAssociatedSchools();
		fetchProfiles();
		fetchVisitTypes();
		fetchVisitStatuses();
		fetchSalesProgressionStages();
	}, []);

	useEffect(() => {
		if (userProfile) {
			fetchVisits();
		}
	}, [userProfile]);

	useEffect(() => {
		applyFilters();
	}, [filters, visits, selectedAcademicYear, userProfile]);

	useEffect(() => {
		setPage(0);
	}, [selectedAcademicYear]);

	const isJustificationRequired = () => {
		if (!selectedVisit || !editForm.visited_date || !editForm.planned_date)
			return false;

		const visitedDate = new Date(editForm.visited_date);
		const plannedDate = new Date(editForm.planned_date);

		visitedDate.setHours(0, 0, 0, 0);
		plannedDate.setHours(0, 0, 0, 0);

		return visitedDate > plannedDate;
	};

	const fetchUserProfile = async () => {
		try {
			setIsLoading(true); // Set loading to true when starting
			const { data, error } = await supabase
				.from("profiles")
				.select("*")
				.eq("id", user.id)
				.single();

			if (error) throw error;
			setUserProfile(data);

			// Fetch visits after getting user profile
			await fetchVisitsWithRole(data);
		} catch (err) {
			console.error("Error fetching user profile:", err);
		} finally {
			setIsLoading(false); // Set loading to false when done
		}
	};

	const fetchVisitsWithRole = async (profile = userProfile) => {
		try {
			let query = supabase.from("school_visits").select(
				`
          *,
          school_types (*),
          schools (*),
          planned_by_profile:profiles!planned_by (*),
          visited_by_profile:profiles!visited_by (*),
          visit_types (*),
          visit_status (*),
          sales_progression_stages (*)
        `
			);

			// Apply role-based filtering
			if (profile?.role !== "ROLE_FUNMIYO_ADMIN") {
				// For non-admin users, only show visits where they are either planned_by or visited_by
				query = query.or(`planned_by.eq.${user.id},visited_by.eq.${user.id}`);
			}

			const { data, error } = await query.order("planned_date", {
				ascending: false,
			});

			if (error) throw error;
			setVisits(data || []);
			setFilteredVisits(data || []);
		} catch (err) {
			console.error("Error fetching visits:", err);
			setErrorMessage("Error loading visits");
		}
	};

	const fetchVisits = async () => {
		try {
			let query = supabase.from("school_visits").select(
				`
          *,
          school_types (*),
          schools (*),
          planned_by_profile:profiles!planned_by (*),
          visited_by_profile:profiles!visited_by (*),
          visit_types (*),
          visit_status (*),
          sales_progression_stages (*)
        `
			);

			// Apply role-based filtering
			if (userProfile?.role !== "ROLE_FUNMIYO_ADMIN") {
				// For non-admin users, only show visits where they are either planned_by or visited_by
				query = query.or(`planned_by.eq.${user.id},visited_by.eq.${user.id}`);
			}

			const { data, error } = await query.order("planned_date", {
				ascending: false,
			});

			if (error) throw error;
			setVisits(data || []);
			setFilteredVisits(data || []);
		} catch (err) {
			console.error("Error fetching visits:", err);
			setErrorMessage("Error loading visits");
		}
	};

	const fetchSchoolTypes = async () => {
		try {
			const { data, error } = await supabase
				.from("school_types")
				.select("*")
				.order("name");
			if (error) throw error;
			setSchoolTypes(data || []);
		} catch (err) {
			console.error("Error fetching school types:", err);
		}
	};

	const fetchSchools = async () => {
		try {
			const { data, error } = await supabase
				.from("schools")
				.select(
					`
				*,
				school_types (
					id,
					name
				)
			`
				)
				.in("status", ["Active", "Pending"])
				.order("name");
			if (error) throw error;
			setSchools(data || []);
		} catch (err) {
			console.error("Error fetching schools:", err);
		}
	};

	const fetchUserAssociatedSchools = async () => {
		try {
			const activeSchools = await fetchActiveSchoolsForExpert(supabase, user.id);
			setUserAssociatedSchools(
				activeSchools.map((school) => ({
					id: school.id,
					name: school.name,
				}))
			);
		} catch (err) {
			console.error("Error fetching associated schools:", err);
		}
	};

	const fetchProfiles = async () => {
		try {
			const { data, error } = await supabase
				.from("profiles")
				.select("*")
				.in("role", ["ROLE_FUNMATH_ADMIN", "ROLE_FUNMIYO_EMPLOYEE"])
				.eq("status", "Active");
			if (error) throw error;
			setProfiles(data || []);
		} catch (err) {
			console.error("Error fetching profiles:", err);
		}
	};

	const fetchVisitTypes = async () => {
		try {
			const { data, error } = await supabase
				.from("visit_types")
				.select("*")
				.eq("status", true)
				.order("name");
			if (error) throw error;
			setVisitTypes(data || []);
		} catch (err) {
			console.error("Error fetching visit types:", err);
		}
	};

	const fetchVisitStatuses = async () => {
		try {
			const { data, error } = await supabase
				.from("visit_status")
				.select("*")
				.order("name");
			if (error) throw error;
			setVisitStatuses(data || []);
		} catch (err) {
			console.error("Error fetching visit statuses:", err);
		}
	};

	const fetchSalesProgressionStages = async () => {
		try {
			const { data, error } = await supabase
				.from("sales_progression_stages")
				.select("*")
				.order("name");
			if (error) throw error;
			setSalesProgressionStages(data || []);
		} catch (err) {
			console.error("Error fetching sales progression stages:", err);
		}
	};

	const applyFilters = () => {
		let filtered = visits.filter((visit) =>
			visitMatchesAcademicYear(visit, selectedAcademicYear)
		);

		if (userProfile?.role !== "ROLE_FUNMIYO_ADMIN") {
			filtered = filtered.filter((visit) => isActiveSchool(visit.schools));
		}

		if (filters.search) {
			const searchTerm = filters.search.toLowerCase();
			filtered = filtered.filter(
				(visit) =>
					visit.schools?.name.toLowerCase().includes(searchTerm) ||
					visit.planned_by_profile?.full_name
						.toLowerCase()
						.includes(searchTerm) ||
					visit.visited_by_profile?.full_name.toLowerCase().includes(searchTerm)
			);
		}

		if (filters.visitStatus) {
			filtered = filtered.filter(
				(visit) => visit.visit_status_id === filters.visitStatus
			);
		}

		setFilteredVisits(filtered);
	};

	const handleAddNew = () => {
		setSelectedVisit(null);
		setSelectedSchool(null);
		setEditForm({
			school_type_id: "",
			school_id: "",
			planned_by: user.id,
			planned_date: dayjs().toISOString(),
			visit_type: "",
			visit_type_id: "",
			visit_status_id: "",
			visited_date: null,
			visited_by: "",
			sales_progression_stage_id: "",
			existing_school_remarks: "",
			new_school_remarks: "",
			oa_report_shared: false,
			assessment_qp_collected: false,
			remarks: "",
			comments: "",
		});
		setOpenDialog(true);
	};

	const handleEdit = (visit) => {
		setSelectedVisit(visit);

		// Find and set the selected school object
		const currentSchool = schools.find(
			(school) => school.id === visit.school_id
		);
		setSelectedSchool(currentSchool || null);

		setEditForm({
			school_type_id: visit.school_type_id,
			school_id: visit.school_id,
			planned_by: visit.planned_by,
			planned_date: visit.planned_date,
			visit_type: visit.visit_types?.visit_type || "",
			visit_type_id: visit.visit_type_id,
			visit_status_id: visit.visit_status_id,
			visited_date: visit.visited_date,
			visited_by: visit.visited_by,
			sales_progression_stage_id: visit.sales_progression_stage_id,
			existing_school_remarks: visit.existing_school_remarks || "",
			new_school_remarks: visit.new_school_remarks || "",
			oa_report_shared: visit.oa_report_shared || false,
			assessment_qp_collected: visit.assessment_qp_collected || false,
			remarks: visit.remarks || "",
			comments: visit.comments || "", // Add this field
		});
		setOpenDialog(true);
	};

	useEffect(() => {
		if (editForm.school_id && !selectedSchool) {
			const school = schools.find((s) => s.id === editForm.school_id);
			if (school) {
				setSelectedSchool(school);
			}
		}
	}, [editForm.school_id, schools, selectedSchool]);

	const getFilteredVisitTypes = () => {
		if (!editForm.visit_type) {
			return visitTypes;
		}
		return visitTypes.filter((vt) => vt.visit_type === editForm.visit_type);
	};

	const handleDownload = async () => {
		try {
			const XLSX = await import("xlsx");

			const exportData = filteredVisits.map((visit) => ({
				School: visit.schools?.name,
				"School Type": visit.school_types?.name,
				"Planned Date": visit.planned_date
					? formatDateTime(visit.planned_date)
					: "",
				"Planned By": visit.planned_by_profile?.full_name,
				"Visit Type": visit.visit_types?.name,
				"Visit Status": visit.visit_status?.name,
				"Visited Date": visit.visited_date
					? formatDateTime(visit.visited_date)
					: "",
				"Visited By": visit.visited_by_profile?.full_name,
				"Sales Stage": visit.sales_progression_stages?.name,
				"Existing School Remarks": visit.existing_school_remarks,
				"New School Remarks": visit.new_school_remarks,
				"OA Report Shared": visit.oa_report_shared ? "Yes" : "No",
				"Assessment QP Collected": visit.assessment_qp_collected ? "Yes" : "No",
			}));

			const ws = XLSX.utils.json_to_sheet(exportData);
			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, "School Visits");
			XLSX.writeFile(wb, "school_visits.xlsx");
		} catch (error) {
			console.error("Error downloading file:", error);
			setErrorMessage("Error downloading file");
		}
	};

	const handleSave = async () => {
		try {
			setIsSaving(true);

			// Validation: Check required fields
			if (!editForm.school_id) {
				setValidationError("School is required.");
				return;
			}
			if (!editForm.planned_by) {
				setValidationError("Planned By is required.");
				return;
			}
			if (!editForm.planned_date) {
				setValidationError("Planned Date is required.");
				return;
			}

			// Sales-specific validation
			if (editForm.visit_type === "Sales") {
				if (!editForm.sales_progression_stage_id) {
					setValidationError(
						"Sales Progression Stage is required for Sales visits."
					);
					return;
				}
				if (!editForm.comments?.trim()) {
					setValidationError("Comments are required for Sales visits.");
					return;
				}
			}

			// Add justification validation
			if (isJustificationRequired() && !editForm.remarks.trim()) {
				setValidationError(
					"Justification is required when visited date is later than planned date."
				);
				return;
			}

			// Clear previous errors if validation passes
			setValidationError("");

			const formData = {
				...editForm,
				visit_type: editForm.visit_type || null,
				visit_type_id: editForm.visit_type_id || null,
				visit_status_id: editForm.visit_status_id || null,
				visited_by: editForm.visited_by || null,
				visited_date: editForm.visited_date || null,
				sales_progression_stage_id: editForm.sales_progression_stage_id || null,
				remarks: editForm.remarks || null,
				comments: editForm.comments || null,
				updated_at: new Date().toISOString(),
			};

			const { error } = selectedVisit
				? await supabase
						.from("school_visits")
						.update(formData)
						.eq("id", selectedVisit.id)
				: await supabase.from("school_visits").insert([formData]);

			if (error) throw error;

			setSuccessMessage(
				selectedVisit
					? "Visit updated successfully"
					: "Visit added successfully"
			);

			await fetchVisits();
			setOpenDialog(false);
			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error saving visit:", err);
			setErrorMessage("Error saving visit.");
		} finally {
			setIsSaving(false);
		}
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// Determine which schools to show based on user role and selected school type
	const getDisplayedSchools = () => {
		let filteredSchools = [];

		if (userProfile?.role === "ROLE_FUNMIYO_ADMIN") {
			filteredSchools = [...schools];
		} else {
			filteredSchools = schools.filter(
				(school) =>
					isActiveSchool(school) &&
					userAssociatedSchools.some((userSchool) => userSchool.id === school.id)
			);
		}

		if (editForm.school_type_id) {
			filteredSchools = filteredSchools.filter(
				(school) => school.school_type_id === editForm.school_type_id
			);
		}

		if (selectedVisit && editForm.school_id) {
			const currentSelectedSchool = schools.find(
				(school) => school.id === editForm.school_id
			);

			if (
				currentSelectedSchool &&
				(userProfile?.role === "ROLE_FUNMIYO_ADMIN" ||
					isActiveSchool(currentSelectedSchool)) &&
				!filteredSchools.some(
					(school) => school.id === currentSelectedSchool.id
				)
			) {
				filteredSchools.push(currentSelectedSchool);
			}
		}

		return filteredSchools;
	};

	return (
		<Box>
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}>
				<Box>
					<Typography variant="h6">School Visits</Typography>
					<Typography variant="body2" color="text.secondary">
						Showing visits for academic year {selectedAcademicYear} (Apr–Mar,
						based on planned date)
					</Typography>
				</Box>
				<Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
					<AcademicYearSelector
						value={selectedAcademicYear}
						onChange={setSelectedAcademicYear}
					/>
					<TextField
						size="small"
						placeholder="Search schools or employees"
						value={filters.search}
						onChange={(e) => setFilters({ ...filters, search: e.target.value })}
						InputProps={{
							startAdornment: <SearchIcon size={20} />,
						}}
					/>
					<FormControl size="small" sx={{ minWidth: 200 }}>
						<InputLabel>Visit Status</InputLabel>
						<Select
							value={filters.visitStatus}
							label="Visit Status"
							onChange={(e) =>
								setFilters({ ...filters, visitStatus: e.target.value })
							}>
							<MenuItem value="">All Statuses</MenuItem>
							{visitStatuses.map((status) => (
								<MenuItem key={status.id} value={status.id}>
									{status.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button
						variant="contained"
						startIcon={<DownloadIcon size={20} />}
						onClick={handleDownload}>
						Export
					</Button>
					<Button
						variant="contained"
						startIcon={<PlusIcon size={20} />}
						onClick={handleAddNew}>
						Add Visit
					</Button>
				</Box>
			</Box>

			<Paper
				elevation={0}
				sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: "8px" }}>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>School</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>School Type</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Planned Date</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Planned By</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Visit Type</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Visit Status</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Visited Date</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Visited By</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Sales Stage</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={11} sx={{ textAlign: "center", py: 4 }}>
										<Typography variant="body1" color="text.secondary">
											Loading visits...
										</Typography>
									</TableCell>
								</TableRow>
							) : filteredVisits.length === 0 ? (
								<TableRow>
									<TableCell colSpan={11} sx={{ textAlign: "center", py: 4 }}>
										<Typography variant="body1" color="text.secondary">
											No visits found for {selectedAcademicYear}
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								filteredVisits
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((visit) => (
										<TableRow
											key={visit.id}
											sx={{ backgroundColor: getRowBackgroundColor(visit) }}>
											<TableCell>
												{visit.visit_types?.visit_type || "Service"}
											</TableCell>
											<TableCell>{visit.schools?.name}</TableCell>
											<TableCell>{visit.school_types?.name}</TableCell>
											<TableCell>{formatDateTime(visit.planned_date)}</TableCell>
											<TableCell>
												{visit.planned_by_profile?.full_name}
											</TableCell>
											<TableCell>{visit.visit_types?.name}</TableCell>
											<TableCell>
												<Chip
													label={visit.visit_status?.name}
													color={
														visit.visit_status?.name === "Done"
															? "success"
															: visit.visit_status?.name === "Scheduled"
															? "primary"
															: "warning"
													}
													size="small"
												/>
											</TableCell>
											<TableCell>{formatDateTime(visit.visited_date)}</TableCell>
											<TableCell
												sx={{
													color:
														visit.planned_by !== visit.visited_by
															? "red"
															: "inherit",
													fontWeight:
														visit.planned_by !== visit.visited_by
															? "bold"
															: "normal",
												}}>
												{visit.visited_by_profile?.full_name || "-"}
											</TableCell>
											<TableCell>
												{visit.sales_progression_stages?.name || "-"}
											</TableCell>
											<TableCell>
												<IconButton
													size="small"
													onClick={() => handleEdit(visit)}
													title="Edit Visit">
													<EditIcon size={16} />
												</IconButton>
											</TableCell>
										</TableRow>
									))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
					<TablePagination
						component="div"
						count={filteredVisits.length}
						page={page}
						onPageChange={handleChangePage}
						rowsPerPage={rowsPerPage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						rowsPerPageOptions={[10, 25, 50, 100]}
					/>
				</Box>
			</Paper>

			<Dialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				maxWidth="md"
				fullWidth>
				<DialogTitle>
					{selectedVisit ? "Edit School Visit" : "Add School Visit"}
				</DialogTitle>
				<DialogContent>
					{validationError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{validationError}
						</Alert>
					)}
					{userAssociatedSchools.length === 0 &&
						userProfile?.role !== "ROLE_FUNMIYO_ADMIN" && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								You don't have any associated schools. Please contact an
								administrator.
							</Alert>
						)}
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Grid container spacing={2} sx={{ mt: 1 }}>
							<Grid item xs={12} sm={6}>
								<FormControl fullWidth>
									<InputLabel>Type</InputLabel>
									<Select
										value={editForm.visit_type} // Changed from 'type' to 'visit_type'
										label="Type"
										onChange={(e) => {
											const newType = e.target.value;
											const currentVisitType = visitTypes.find(
												(vt) => vt.id === editForm.visit_type_id
											);

											setEditForm({
												...editForm,
												visit_type: newType, // Changed from 'type' to 'visit_type'
												visit_type_id:
													currentVisitType &&
													currentVisitType.visit_type === newType
														? editForm.visit_type_id
														: "",
											});
										}}>
										<MenuItem value="">Select Type</MenuItem>
										{visitTypeCategories.map((type) => (
											<MenuItem key={type} value={type}>
												{type}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormControl fullWidth>
									<InputLabel>School Type</InputLabel>
									<Select
										value={editForm.school_type_id}
										label="School Type"
										onChange={(e) => {
											// When school type changes, reset the school selection
											// if the currently selected school doesn't match the new type
											const newSchoolTypeId = e.target.value;
											let updatedForm = {
												...editForm,
												school_type_id: newSchoolTypeId,
												// Always reset school_id when school type changes to ensure consistency
												school_id: "",
											};

											setEditForm(updatedForm);
										}}>
										<MenuItem value="">Select School Type</MenuItem>
										{schoolTypes.map((type) => (
											<MenuItem key={type.id} value={type.id}>
												{type.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
									<Autocomplete
										fullWidth
										options={getDisplayedSchools()}
										getOptionLabel={(option) => option.name || ""}
										value={selectedSchool}
										onChange={(event, newValue) => {
											setSelectedSchool(newValue);
											setEditForm({
												...editForm,
												school_id: newValue ? newValue.id : "",
											});
										}}
										disabled={
											!editForm.school_type_id ||
											(userAssociatedSchools.length === 0 &&
												userProfile?.role !== "ROLE_FUNMIYO_ADMIN")
										}
										renderInput={(params) => (
											<TextField
												{...params}
												label="School"
												placeholder={
													!editForm.school_type_id
														? "Select a school type first"
														: getDisplayedSchools().length === 0
														? "No schools available for this type"
														: "Search schools..."
												}
											/>
										)}
										renderOption={(props, option) => (
											<Box component="li" {...props}>
												<Box>
													<Typography variant="body2">{option.name}</Typography>
													<Typography variant="caption" color="text.secondary">
														{option.school_types?.name} •{" "}
														{option.district || "No district"}
													</Typography>
												</Box>
											</Box>
										)}
										noOptionsText={
											!editForm.school_type_id
												? "Select a school type first"
												: "No schools found"
										}
										filterOptions={(options, { inputValue }) => {
											const filtered = options.filter(
												(option) =>
													option.name
														.toLowerCase()
														.includes(inputValue.toLowerCase()) ||
													(option.district &&
														option.district
															.toLowerCase()
															.includes(inputValue.toLowerCase()))
											);
											return filtered;
										}}
									/>
									{/* Show + button only if Prospective School is selected */}
									{!selectedVisit &&
										editForm.school_type_id &&
										schoolTypes.find(
											(type) => type.id === editForm.school_type_id
										)?.name === "Prospective School" && (
											<IconButton
												color="primary"
												onClick={handleAddSchoolClick}
												sx={{ mt: 1 }}
												title="Add New School">
												<PlusIcon size={20} />
											</IconButton>
										)}
								</Box>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth>
									<InputLabel>Planned By</InputLabel>
									<Select
										value={editForm.planned_by}
										label="Planned By"
										onChange={(e) =>
											setEditForm({ ...editForm, planned_by: e.target.value })
										}>
										{profiles.map((profile) => (
											<MenuItem key={profile.id} value={profile.id}>
												{profile.full_name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={6}>
								<DateTimePicker
									label="Planned Date & Time"
									value={
										editForm.planned_date ? dayjs(editForm.planned_date) : null
									}
									onChange={(date) =>
										setEditForm({
											...editForm,
											planned_date: date ? date.toISOString() : null,
										})
									}
									disabled={!!selectedVisit}
									ampm={false}
									slotProps={{ textField: { fullWidth: true } }}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth>
									<InputLabel>Visit Type</InputLabel>
									<Select
										value={editForm.visit_type_id}
										label="Visit Type"
										onChange={(e) =>
											setEditForm({
												...editForm,
												visit_type_id: e.target.value,
											})
										}
										disabled={!editForm.visit_type}>
										{" "}
										{editForm.visit_type ? (
											getFilteredVisitTypes().length > 0 ? (
												getFilteredVisitTypes().map((type) => (
													<MenuItem key={type.id} value={type.id}>
														{type.name}
													</MenuItem>
												))
											) : (
												<MenuItem value="" disabled>
													No visit types available for this type
												</MenuItem>
											)
										) : (
											<MenuItem value="" disabled>
												Select a type first
											</MenuItem>
										)}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth>
									<InputLabel>Visit Status</InputLabel>
									<Select
										value={editForm.visit_status_id}
										label="Visit Status"
										onChange={(e) =>
											setEditForm({
												...editForm,
												visit_status_id: e.target.value,
											})
										}>
										{visitStatuses.map((status) => (
											<MenuItem key={status.id} value={status.id}>
												{status.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={6}>
								<DateTimePicker
									label="Visited Date & Time"
									value={
										editForm.visited_date ? dayjs(editForm.visited_date) : null
									}
									onChange={(date) =>
										setEditForm({
											...editForm,
											visited_date: date ? date.toISOString() : null,
										})
									}
									ampm={false}
									slotProps={{
										textField: { fullWidth: true },
										field: { clearable: true },
									}}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth>
									<InputLabel>Visited By</InputLabel>
									<Select
										value={editForm.visited_by || ""}
										label="Visited By"
										onChange={(e) =>
											setEditForm({
												...editForm,
												visited_by: e.target.value || null,
											})
										}>
										<MenuItem value="">Not Assigned</MenuItem>
										{profiles.map((profile) => (
											<MenuItem key={profile.id} value={profile.id}>
												{profile.full_name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							{editForm.visit_type === "Sales" && (
								<Grid item xs={12}>
									<FormControl fullWidth required>
										<InputLabel>Sales Progression Stage *</InputLabel>
										<Select
											value={editForm.sales_progression_stage_id}
											label="Sales Progression Stage *"
											onChange={(e) =>
												setEditForm({
													...editForm,
													sales_progression_stage_id: e.target.value,
												})
											}
											error={
												editForm.visit_type === "Sales" &&
												!editForm.sales_progression_stage_id
											}>
											<MenuItem value="">Select Sales Stage</MenuItem>
											{salesProgressionStages.map((stage) => (
												<MenuItem key={stage.id} value={stage.id}>
													{stage.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
							)}

							{editForm.school_type_id === "existing_school_type_id" && (
								<Grid item xs={12}>
									<TextField
										label="Existing School Remarks"
										multiline
										rows={3}
										fullWidth
										value={editForm.existing_school_remarks}
										onChange={(e) =>
											setEditForm({
												...editForm,
												existing_school_remarks: e.target.value,
											})
										}
									/>
								</Grid>
							)}

							{editForm.school_type_id === "new_school_type_id" && (
								<Grid item xs={12}>
									<TextField
										label="New School Remarks"
										multiline
										rows={3}
										fullWidth
										value={editForm.new_school_remarks}
										onChange={(e) =>
											setEditForm({
												...editForm,
												new_school_remarks: e.target.value,
											})
										}
									/>
								</Grid>
							)}

							<Grid item xs={12} sm={6}>
								<FormControlLabel
									control={
										<Checkbox
											checked={editForm.oa_report_shared}
											onChange={(e) =>
												setEditForm({
													...editForm,
													oa_report_shared: e.target.checked,
												})
											}
										/>
									}
									label="OA Report Shared"
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControlLabel
									control={
										<Checkbox
											checked={editForm.assessment_qp_collected}
											onChange={(e) =>
												setEditForm({
													...editForm,
													assessment_qp_collected: e.target.checked,
												})
											}
										/>
									}
									label="Assessment QP Collected"
								/>
							</Grid>

							<Grid item xs={12}>
								<TextField
									label={
										editForm.visit_type === "Sales" ? "Comments *" : "Comments"
									}
									multiline
									rows={4}
									fullWidth
									required={editForm.visit_type === "Sales"}
									value={editForm.comments}
									onChange={(e) =>
										setEditForm({
											...editForm,
											comments: e.target.value,
										})
									}
									placeholder="Add any additional comments about this visit..."
									error={
										editForm.visit_type === "Sales" &&
										!editForm.comments?.trim()
									}
									helperText={
										editForm.visit_type === "Sales" &&
										!editForm.comments?.trim()
											? "Comments are required for Sales visits"
											: editForm.visit_type === "Sales"
											? "Please provide detailed comments for this sales visit"
											: ""
									}
								/>
							</Grid>

							{isJustificationRequired() && (
								<Grid item xs={12}>
									<TextField
										label="Justification for Late Visit *"
										multiline
										rows={3}
										fullWidth
										required
										value={editForm.remarks}
										onChange={(e) =>
											setEditForm({
												...editForm,
												remarks: e.target.value,
											})
										}
										error={
											isJustificationRequired() && !editForm.remarks.trim()
										}
										helperText={
											isJustificationRequired() && !editForm.remarks.trim()
												? "Justification is required when visited date is later than planned date"
												: "Please provide justification for visiting later than planned"
										}
									/>
								</Grid>
							)}
						</Grid>
					</LocalizationProvider>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setOpenDialog(false)}
						disabled={isSaving} // Add disabled prop
					>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						disabled={
							isSaving || // Add this condition
							(userAssociatedSchools.length === 0 &&
								userProfile?.role !== "ROLE_FUNMIYO_ADMIN")
						}>
						{isSaving
							? selectedVisit
								? "Updating..."
								: "Saving..."
							: selectedVisit
							? "Update"
							: "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openAddSchoolDialog}
				onClose={() => setOpenAddSchoolDialog(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>Add New Prospective School</DialogTitle>
				<DialogContent>
					<Grid container spacing={2} sx={{ mt: 1 }}>
						<Grid item xs={12}>
							<TextField
								label="School Name"
								fullWidth
								required
								value={newSchoolForm.name}
								onChange={(e) => {
									setNewSchoolForm({ ...newSchoolForm, name: e.target.value });
									if (e.target.value.trim()) {
										setNewSchoolErrors({ ...newSchoolErrors, name: false });
									}
								}}
								error={newSchoolErrors.name}
								helperText={
									newSchoolErrors.name ? "School name is required" : ""
								}
							/>
						</Grid>
						<Grid item xs={12}>
							<FormControl fullWidth required error={newSchoolErrors.state}>
								<InputLabel>State / UT</InputLabel>
								<Select
									value={newSchoolForm.state}
									label="State / UT"
									onChange={(e) => {
										const v = e.target.value;
										setNewSchoolForm({
											...newSchoolForm,
											state: v,
											district: "",
										});
										if (v) {
											setNewSchoolErrors({
												...newSchoolErrors,
												state: false,
												district: false,
											});
										}
									}}>
									<MenuItem value="">
										<em>Select state</em>
									</MenuItem>
									{INDIAN_STATES.map((s) => (
										<MenuItem key={s.stateCode} value={s.stateName}>
											{s.stateName}
										</MenuItem>
									))}
								</Select>
								{newSchoolErrors.state && (
									<Typography
										variant="caption"
										color="error"
										sx={{ ml: 2, mt: 0.5 }}>
										State is required
									</Typography>
								)}
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<FormControl
								fullWidth
								required
								error={newSchoolErrors.district}
								disabled={!newSchoolForm.state}>
								<InputLabel>District</InputLabel>
								<Select
									value={newSchoolForm.district}
									label="District"
									onChange={(e) => {
										setNewSchoolForm({
											...newSchoolForm,
											district: e.target.value,
										});
										if (e.target.value) {
											setNewSchoolErrors({
												...newSchoolErrors,
												district: false,
											});
										}
									}}>
									<MenuItem value="">
										<em>
											{newSchoolForm.state
												? "Select district"
												: "Select state first"}
										</em>
									</MenuItem>
									{newSchoolDistrictOptions.map((district) => (
										<MenuItem key={district} value={district}>
											{district}
										</MenuItem>
									))}
								</Select>
								{newSchoolErrors.district && (
									<Typography
										variant="caption"
										color="error"
										sx={{ ml: 2, mt: 0.5 }}>
										District is required
									</Typography>
								)}
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Address"
								fullWidth
								required
								multiline
								rows={3}
								value={newSchoolForm.address}
								onChange={(e) => {
									setNewSchoolForm({
										...newSchoolForm,
										address: e.target.value,
									});
									if (e.target.value.trim()) {
										setNewSchoolErrors({ ...newSchoolErrors, address: false });
									}
								}}
								error={newSchoolErrors.address}
								helperText={
									newSchoolErrors.address ? "Address is required" : ""
								}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setOpenAddSchoolDialog(false)}
						disabled={isSaving} // Add disabled prop
					>
						Cancel
					</Button>
					<Button
						onClick={handleSaveNewSchool}
						variant="contained"
						disabled={isSaving} // Add disabled prop
					>
						{isSaving ? "Adding..." : "Add School"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default SchoolVisits;
